import type { ReactNode } from 'react';
import { cn } from '@/lib/format';

export interface PageHeaderProps {
  /** Usually text; a node is allowed for an inline marker such as an event colour dot. */
  title: ReactNode;
  description?: string;
  overline?: string;
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  overline,
  actions,
  className,
}: PageHeaderProps) {
  return (
    // data-tour anchors live here rather than on each page: every page uses
    // this header, so the guided tour gets a title and an actions target
    // everywhere from one place.
    <header
      data-tour="page-header"
      className={cn('flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8', className)}
    >
      <div>
        {overline && (
          <p className="text-overline uppercase text-ink-subtle font-medium tracking-wider">
            {overline}
          </p>
        )}
        <h1 className="text-section font-display font-semibold mt-1">{title}</h1>
        {description && (
          <p className="text-body text-ink-subtle mt-2 max-w-2xl">{description}</p>
        )}
      </div>
      {actions && (
        <div data-tour="page-actions" className="flex items-center gap-2">
          {actions}
        </div>
      )}
    </header>
  );
}
